/* eslint-disable string-to-lingui/missing-lingui-transformation */
import { t } from '@lingui/macro'
import { CandidateDTO } from '@om1/aspen-api'
import { AspenCandidatesService, CalendarService } from '@om1/aspen-api/codegen/client'
import { CandidateIndexDTO } from '@om1/aspen-api/codegen/client/models/CandidateIndexDTO'
import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { generateCalendarInvite } from '../utils/generate-calendar-invite'

/**
 * Either candidate shape works — the create flow only needs the id,
 * (optional) friendlyId and the preferred-contact window, all of which exist
 * on both the list (``CandidateIndexDTO``) and detail (``CandidateDTO``) DTOs.
 */
export type CalendarEventCandidate = CandidateDTO | CandidateIndexDTO

/**
 * Shared Google-Calendar-first create flow used by both the candidate **list**
 * (icon button) and the candidate **detail** page (labeled button) so the two
 * stay in sync. Encapsulates: preferred-time validation, click telemetry, the
 * Google create call (including the candidate-dashboard deep link in the
 * description), opening the created event, the .ics fallback when Google fails,
 * and the calendar-download audit bump.
 *
 * The list-only "already downloaded" confirmation dialog stays in the calling
 * component because it depends on fields (``calendarDownloadCount`` etc.) that
 * only the list DTO carries.
 */
export function useCreateCalendarEvent({
    candidate,
    onDownloaded
}: {
    // Nullable so callers can invoke the hook before their candidate has
    // loaded (e.g. the detail page fetches asynchronously). ``triggerCreate``
    // no-ops until a candidate is present.
    candidate: CalendarEventCandidate | null | undefined
    onDownloaded?: () => void
}) {
    const [loading, setLoading] = useState(false)
    const { enqueueSnackbar } = useSnackbar()

    const triggerCreate = async () => {
        // Guard against double-clicks while the create call is in flight.
        if (loading) return
        setLoading(true)
        try {
            await runTrigger()
        } finally {
            // ``finally`` so the spinner clears regardless of success / fallback /
            // unhandled error — otherwise a thrown exception (or an early
            // return inside ``runTrigger``) would leave the button stuck on
            // the loader forever.
            setLoading(false)
        }
    }

    const runTrigger = async () => {
        // No candidate yet (still loading) — nothing to schedule.
        if (!candidate) return

        // Validate the candidate has a preferred contact window — without it,
        // there's nothing to put on the calendar.
        if (!candidate.preferredContactStartDatetime || !candidate.preferredContactEndDatetime) {
            enqueueSnackbar(t`No preferred contact time set for this candidate. Please set one before creating a calendar event.`, {
                variant: 'warning'
            })
            return
        }

        // The DTO sends ISO 8601 strings; ``new Date(...).toISOString()`` always
        // produces ``...Z`` form which the backend regex accepts.
        const startISO = new Date(candidate.preferredContactStartDatetime).toISOString()
        const endISO = new Date(candidate.preferredContactEndDatetime).toISOString()

        // Use candidate id only — no PHI (names, email, etc.) on the shared calendar.
        const candidateId = candidate.friendlyId ?? candidate.id

        // Deep link back to this candidate's dashboard so coordinators can jump
        // straight from the calendar event to the candidate page. ``candidate.id``
        // (not friendlyId) keys the ``/candidates/:id`` route; the page is auth-gated,
        // so an unauthenticated click lands on the Auth0 login first.
        const dashboardLink = `${window.location.origin}/candidates/${candidate.id}`

        // The patient's number, on the event itself (APP-1331).
        //
        // Studies that do not use conferencing are calling the patient, so at
        // the moment the reminder fires the number is the one thing needed.
        // Without it the event carried only the dashboard link, so a
        // coordinator had to open aspen to find it -- fine at a desk, not when
        // a call is due.
        //
        // It rides in the description rather than anywhere structured because
        // that is what Google's own reminder email quotes back.
        const phoneLine = candidate.phone ? `\n\nPatient phone: ${candidate.phone}` : ''

        // --- Telemetry: click ----------------------------------------------
        // Mint a UUID per click and forward it as ``X-Correlation-Id`` on the
        // create call so the backend can stitch click → success/failure rows
        // into one logical action. Fired only on the Google path — the .ics
        // fallback below intentionally does not emit a click row, which is
        // how the dashboard separates the two flows.
        const correlationId =
            typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`
        // Fire-and-forget — if the click endpoint is briefly down we still
        // want the user's calendar event to go through. Errors are logged
        // for the dashboard's "missing click" investigations.
        void CalendarService.recordCalendarTelemetryClickCalendarTelemetryClickPost({
            requestBody: {
                correlationId,
                candidateId: candidate.id
            }
        }).catch((err) => {
            console.warn('Failed to record calendar click telemetry', err)
        })

        try {
            const response = await CalendarService.createCalendarEventCalendarEventsPost({
                requestBody: {
                    title: `Patient contact: ${candidateId}`,
                    description: `Scheduled patient contact. Candidate ID: ${candidateId}.${phoneLine}\n\nCandidate dashboard: ${dashboardLink}`,
                    start: startISO,
                    end: endISO,
                    attendees: [],
                    reminders: [
                        { method: 'popup', minutes: 15 },
                        { method: 'email', minutes: 60 }
                    ],
                    addMeet: false,
                    // No attendees — event stays on the shared study calendar only.
                    sendUpdates: 'none',
                    // Domain-wide delegation: impersonate the calendar bot
                    // so Google sees a real user as the organizer.
                    // TODO: pull from env config so non-deiddev envs use
                    // their own bot user.
                    subject: 'calendar-bot@om1.com'
                },
                // Telemetry headers — let the backend log the create_success /
                // create_failure row against the same ``correlationId`` we just
                // posted to /telemetry/click, and tag the candidate.
                xCorrelationId: correlationId,
                xTelemetryCandidateId: candidate.id
            })
            enqueueSnackbar(t`Calendar event created.`, { variant: 'success' })

            // Open the Google Calendar event in a new tab so the coordinator
            // can immediately review or edit it. Delay so (a) the success
            // toast is visible first and (b) Google's backend has time to
            // propagate the event before the user lands on the page —
            // otherwise the calendar grid behind the event-detail popup
            // can show stale data and the new event won't appear on the
            // timeline until the user reloads.
            //
            // Wrap in a guard because ``htmlLink`` is typed as nullable and
            // popup blockers can return ``null`` from ``window.open`` —
            // especially likely with a delayed ``setTimeout`` since the
            // call is no longer inside the user's direct click handler.
            if (response?.htmlLink) {
                const link = response.htmlLink
                window.setTimeout(() => {
                    const opened = window.open(link, '_blank', 'noopener,noreferrer')
                    if (!opened) {
                        enqueueSnackbar(t`Pop-ups are blocked — open the event manually from your Google Calendar.`, {
                            variant: 'info'
                        })
                    }
                }, 1500)
            }

            // Bump the candidate's calendar-download audit counter on the
            // Google success path too, not just the .ics fallback. This is
            // what drives ``calendarDownloadCount`` on ``CandidateIndexDTO``
            // — the same field that flips the icon to green and powers the
            // "Downloaded N times — last by X on Y" tooltip / dialog.
            // Failure here only loses the audit row; the user's calendar
            // event was already created.
            try {
                await AspenCandidatesService.recordCalendarDownloadCandidatesCandidateIdCalendarDownloadsPost({
                    candidateId: candidate.id
                })
            } catch (recordErr) {
                console.error('Failed to record calendar download (Google path)', recordErr)
            }

            onDownloaded?.()
            return
        } catch (err) {
            // Google Calendar create failed (network, 403, 5xx, etc.).
            // Fall back to the legacy flow so the coordinator still gets a
            // calendar invite they can import manually, and record the
            // download for the audit trail.

            enqueueSnackbar(t`Could not create the Google Calendar event — downloading an ICS file instead.`, { variant: 'warning' })

            generateCalendarInvite(candidate, enqueueSnackbar)

            try {
                await AspenCandidatesService.recordCalendarDownloadCandidatesCandidateIdCalendarDownloadsPost({
                    candidateId: candidate.id
                })
            } catch (recordErr) {
                console.error('Failed to record calendar download', recordErr)
            }
            onDownloaded?.()
        }
    }

    return { triggerCreate, loading }
}
